// src/components/Entregas/OrdenesServiciosExcel.jsx
import React from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button, Tooltip, message } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { getOrdenesServiciosDetalle } from "../../services/entregas/servicesOrdenesCompra";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().slice(0, 10); // YYYY-MM-DD
  } catch {
    return iso;
  }
};

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return 0;
  return parseFloat(value);
};

const OrdenesServiciosExcel = ({ oidOrden, ordenes }) => {
  const exportToExcel = async () => {
    try {
      const resp = await getOrdenesServiciosDetalle(oidOrden);
      if (resp.status !== 200) {
        throw new Error(resp.message || "Error al obtener datos para Excel");
      }
      const orders = Array.isArray(resp.data) ? resp.data : [resp.data];

      // 2) Crear workbook
      const workbook = new ExcelJS.Workbook();

      if (oidOrden) {
        // Caso específico: una sola hoja con el detalle de la orden
        crearHojaDetalle(workbook, orders, "Órdenes de Servicio");
      } else {
        // Caso general: crear dos hojas
        // Primera hoja: Resumen con los datos de 'ordenes'
        crearHojaResumen(workbook, ordenes);

        // Obtener números de orden del resumen para filtrar
        const numerosOrdenResumen = Array.isArray(ordenes)
          ? ordenes.map((orden) => orden.numero_orden).filter(Boolean)
          : [];

        // Segunda hoja: Detalle filtrado solo con las órdenes del resumen
        const ordersFiltradas = filtrarOrdenesPorNumero(
          orders,
          numerosOrdenResumen
        );
        crearHojaDetalle(workbook, ordersFiltradas, "Detalle Completo");
      }

      // 3) Descargar
      const buf = await workbook.xlsx.writeBuffer();
      const fileName = oidOrden
        ? `OrdenServicio_${oidOrden}.xlsx`
        : "OrdenesServicio.xlsx";
      saveAs(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName
      );
    } catch (err) {
      console.error("❌ Error exportando a Excel:", err);
      message.error(err?.message || "Error exportando a Excel");
    }
  };

  const filtrarOrdenesPorNumero = (orders, numerosOrdenPermitidos) => {
    if (!Array.isArray(orders) || numerosOrdenPermitidos.length === 0) {
      return orders;
    }

    return orders.filter((order) => {
      const numeroOrden = order.numero_orden;
      return numerosOrdenPermitidos.includes(numeroOrden);
    });
  };

  const crearHojaResumen = (workbook, ordenes) => {
    const sheet = workbook.addWorksheet("Resumen Órdenes");

    // Cabecera para resumen de órdenes
    const header = [
      "Número Orden",
      "Fecha PO",
      "Fecha Registro",
      "Observaciones",
      "Proveedor ID",
      "Proveedor Nombre",
      "Identificación",
      "Dirección",
      "Correo",
      "Teléfono",
      "Valor Total",
      "Total IVA",
      "Valor Total con IVA",
    ];
    const headerRow = sheet.addRow(header);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE6F3FF" },
    };

    // Rellenar datos del resumen
    if (Array.isArray(ordenes)) {
      ordenes.forEach((orden) => {
        sheet.addRow([
          orden.numero_orden ?? "",
          formatDate(orden.fecha_po),
          formatDate(orden.fechaRegistro),
          orden.observaciones ?? "",
          orden.proveedor_id ?? "",
          orden.proveedor_nombre ?? "",
          orden.identificacion ?? "",
          orden.direccion ?? "",
          orden.correo ?? "",
          orden.telefono ?? "",
          formatCurrency(orden.valortotal),
          formatCurrency(orden.total_iva),
          formatCurrency(orden.valortotal_con_iva),
        ]);
      });
    }

    // Aplicar formatos específicos para el resumen
    sheet.getColumn(4).numFmt = "yyyy-mm-dd"; // Fecha PO
    sheet.getColumn(5).numFmt = "yyyy-mm-dd"; // Fecha Registro
    sheet.getColumn(13).numFmt = '"$"#,##0.00'; // Valor Total
    sheet.getColumn(14).numFmt = '"$"#,##0.00'; // Total IVA
    sheet.getColumn(15).numFmt = '"$"#,##0.00'; // Valor Total con IVA

    // Autoajuste de columnas
    sheet.columns.forEach((col) => {
      let max = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const v = cell.value == null ? "" : String(cell.value);
        if (v.length > max) max = v.length;
      });
      col.width = Math.min(max + 2, 50);
    });
  };

  const crearHojaDetalle = (workbook, orders, nombreHoja) => {
    const sheet = workbook.addWorksheet(nombreHoja);

    // 3) Cabecera (solo campos disponibles)
    const header = [
      "Proveedor Nombre",
      "Identificación",
      "Dirección",
      "Correo",
      "Teléfono",
      "Número Orden",
      "Fecha PO",
      "Fecha Registro",
      "Observación PO",
      "Producto (Descripción)",
      "Unidad",
      "Cantidad",
      "Valor Unitario",
      "% IVA",
      "Valor IVA",
      "Valor Unitario con IVA",
      "Total sin IVA",
      "Total con IVA",
    ];
    const headerRow = sheet.addRow(header);
    headerRow.font = { bold: true };

    // Color diferente si es la hoja de detalle completo
    if (nombreHoja === "Detalle Completo") {
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE6FFE6" },
      };
    }

    // 4) Rellenar filas
    orders.forEach((o) => {
      const {
        oid,
        oidProveedor,
        proveedor,
        numero_orden,
        fecha_po,
        fechaRegistro,
        observaciones,
        detalles,
      } = o;

      const prov = proveedor || {};
      const baseRow = [
        prov.nombre ?? "",
        prov.identificacion ?? "",
        prov.direccion ?? "",
        prov.correo ?? "",
        prov.telefono ?? "",
        numero_orden ?? "",
        formatDate(fecha_po),
        formatDate(fechaRegistro),
        observaciones ?? "",
      ];

      if (Array.isArray(detalles) && detalles.length > 0) {
        detalles.forEach((d) => {
          const cantidad = d.cantidad ?? 0;
          const valorUnitario = d.valor_unitario ?? 0;
          const ivaPorcentaje = d.valorIVA ?? 0; // Ej: 19 = 19%

          // Calcular IVA en dinero
          const valorIVA = (valorUnitario * ivaPorcentaje) / 100;

          // Valor unitario + IVA
          const valorUnitarioConIVA = valorUnitario + valorIVA;

          // Subtotal (cantidad * valor unitario sin IVA)
          const subtotal = cantidad * valorUnitario;
          const subtotalIVA = cantidad * valorUnitarioConIVA;

          sheet.addRow([
            ...baseRow,
            d.producto ?? "",
            d.unidadNombre ?? "",
            cantidad,
            valorUnitario, // valor base
            ivaPorcentaje, // % IVA
            valorIVA, // $ IVA calculado
            valorUnitarioConIVA, // unitario con IVA
            subtotal, // total * cantidad
            subtotalIVA,
          ]);
        });
      } else {
        // Orden sin detalles: columnas de detalle vacías
        sheet.addRow([...baseRow, "", "", "", "", "", "", "", "", "", "", ""]);
      }
    });

    // 5) Formatos y anchos
    sheet.getColumn(7).numFmt = "yyyy-mm-dd"; // Fecha PO
    sheet.getColumn(8).numFmt = "yyyy-mm-dd"; // Fecha Registro
    sheet.getColumn(15).numFmt = '"$"#,##0.00'; // Valor Unitario
    sheet.getColumn(17).numFmt = '"$"#,##0.00'; // Retención
    sheet.getColumn(18).numFmt = '"$"#,##0.00'; // Valor Unitario con IVA
    sheet.getColumn(19).numFmt = '"$"#,##0.00'; // Subtotal
    sheet.getColumn(20).numFmt = '"$"#,##0.00'; // Subtotal con IVA

    // Autoajuste de columnas
    sheet.columns.forEach((col) => {
      let max = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const v = cell.value == null ? "" : String(cell.value);
        if (v.length > max) max = v.length;
      });
      col.width = Math.min(max + 2, 40);
    });
  };

  return (
    <Tooltip title="Exportar datos">
      <Button
        type="primary"
        icon={<FileExcelOutlined style={oidOrden ? { fontSize: 19 } : {}} />}
        onClick={exportToExcel}
      >
        {oidOrden ? null : "Exportar"}
      </Button>
    </Tooltip>
  );
};

export default OrdenesServiciosExcel;
